<template>
  <fragment>
    <thead>
      <tr>
        <th
          v-for="col in displayColumns"
          :key="col.id"
          @click="sortTable(col.id)"
        >
          {{ col.display }}
          <font-awesome-icon
            :icon="['fas', 'sort']"
            class="icon alt"
          />
          <span
            v-if="col.id == sortColumn"
            :class="ascending ? 'active-down' : 'active-up'"
          >
            <font-awesome-icon
              :icon="['fas', 'sort-down']"
              class="icon alt"
            />
            <font-awesome-icon
              :icon="['fas', 'sort-up']"
              class="icon alt"
            />
          </span>
        </th>
        <th class="text-right actions">
          <span class="sr-only">Actions</span>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="testFixture in filteredTestFixturesData"
        :key="testFixture.id"
      >
        <td
          v-for="col in displayColumns"
          :key="testFixture.id + '-' + col.id"
          :style="col.style"
          :title="col.title ? col.title(testFixture[col.id], testFixture) : null"
          :class="col.classFn ?
            col.classFn(testFixture[col.id], classFunctionProps)
            : col.class"
        >
          <span
            :class="col.classFn ? col.classFn(testFixture[col.id], classFunctionProps) : col.class"
            @click="handleColumnClick(col, testFixture)"
          >
            {{ col.format ? col.format(testFixture[col.id]) : testFixture[col.id] }}
            <span
              v-if="col.id === 'softwareVersion'
                && $store.getters.isAdminUser
                && latestTestFixtureVersion
                && testFixture.softwareVersion
                && testFixture.softwareVersion !== latestTestFixtureVersion"
            >
              <font-awesome-icon
                :icon="['fas', 'exclamation-circle']"
                class="icon alt"
              />
            </span>
          </span>
          <label class="d-block d-md-none">{{ col.display }}</label>
        </td>
        <td class="actions">
          <button
            v-if="$store.getters.isKgbAdminUser && testFixture.disabled"
            v-b-tooltip.hover
            class="green-hover"
            title="Enable Test Fixture"
            type="button"
            style="margin-right: 3px;"
            @click="enableTestFixture(testFixture)"
          >
            <font-awesome-icon
              :icon="['fas', 'power-off']"
              class="icon alt"
            />
          </button>

          <button
            v-if="$store.getters.isAdminUser"
            v-b-tooltip.hover
            type="button"
            class="blue-hover"
            title="Edit Test Fixture"
            style="margin-right: 3px;"
            @click="showEditModal(testFixture)"
          >
            <font-awesome-icon
              :icon="['fas', 'pencil']"
              class="icon alt"
            />
          </button>

          <button
            v-if="$store.getters.isAdminUser
              && latestTestFixtureVersion
              && testFixture.softwareVersion
              && testFixture.softwareVersion !== latestTestFixtureVersion"
            v-b-tooltip.hover
            type="button"
            class="blue-hover"
            style="margin-right: 3px;"
            title="Push update"
            @click="verifyUpdateToVersion(testFixture)"
          >
            <font-awesome-icon
              :icon="['fal', 'layer-plus']"
              class="icon alt"
            />
          </button>
        </td>
      </tr>
    </tbody>
    <tfoot v-if="testFixtures.length > recordsPerPage">
      <tr>
        <td :colspan="columns.length + ($store.getters.isKgbAdminUser ? 1 : 0)">
          <b-pagination
            v-model="currentPage"
            :total-rows="testFixtures.length"
            :per-page="recordsPerPage"
            @change="pageChange"
          />
        </td>
      </tr>
    </tfoot>

    <!-- Checkins Modal -->
    <b-modal
      id="checkin-modal"
      ref="checkinModal"
      title="Last Test Fixture Check Ins"
      ok-only
      ok-title="Close"
      size="md"
    >
      <div>
        <table class="table data-download-table">
          <tbody>
            <tr
              v-for="checkin in checkins"
              :key="checkin.created"
            >
              <td :title="new Date(checkin.created).toUTCString()">
                {{ new Date(checkin.created).toLocaleString() }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <!-- Confirmation Modal -->
    <confirm-modal
      ref="confirmModal"
      :title="confirmTitle"
      :body="confirmBody"
      :on-confirm="confirmAction"
    />
  </fragment>
</template>

<script>
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import paginationHelper from '../../common/paginationHelper';

/* eslint-disable vue/require-default-prop */
export default {
  name: 'TestFixturesTable',
  components: {
    ConfirmModal,
  },
  props: {
    testFixtures: {
      type: Array,
    },
    latestTestFixtureVersion: {
      type: String,
    },
    showEditModal: {
      type: Function,
    },
    enableTestFixture: {
      type: Function,
    },
    testFixtureCheckins: {
      type: Function,
    },
    updateToLatestVersion: {
      type: Function,
    },
  },
  data: () => ({
    ascending: false,
    sortColumn: '',
    classFnProps: {},
    checkins: [],
    columns: [
      {
        id: 'serialNumber', display: 'Fixture SN',
      },
      {
        id: 'organizationName', display: 'Organization', adminOnly: true,
      },
      {
        id: 'siteName', display: 'Site', orgAdminOnly: true,
      },
      {
        id: 'lastCheckIn',
        display: 'Last Checkin',
        format: (v) => {
          if (!v) {
            return 'N/A';
          }
          const d = new Date(v);
          return d.toLocaleString();
        },
        title: (v) => {
          if (!v) {
            return '';
          }
          const d = new Date(v);
          return d.toUTCString();
        },
        classFn: value => (value ? 'c-pointer' : ''),
        isCheckinLink: true,
      },
      {
        id: 'softwareVersion',
        display: 'Software Version',
        classFn: (value, props) => (value && value !== props.latestTestFixtureVersion ? 'error-red' : ''),
        format: value => (value || 'N/A'),
      },
      {
        id: 'disabled',
        display: 'Enabled',
        classFn: value => (value ? 'error-red' : ''),
        format: value => (value ? 'Disabled' : 'Enabled'),
      },
    ],
    currentPage: 1,
    recordsPerPage: 25,
    confirmTitle: '',
    confirmBody: '',
    confirmAction: () => {},
  }),
  computed: {
    filteredTestFixturesData() {
      return this.sortedData().slice(
        (this.currentPage - 1) * this.recordsPerPage,
        this.currentPage * this.recordsPerPage,
      ) || [];
    },
    displayColumns() {
      return this.columns
        .filter(x => (!x.adminOnly || this.$store.getters.isKgbAdminUser)
            && (!x.orgAdminOnly || this.$store.getters.isAdminUser));
    },
    classFunctionProps() {
      return {
        latestTestFixtureVersion: this.latestTestFixtureVersion,
      };
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$route.params': function () {
      this.handleRouteParams(this.$route.query);
    },
  },
  mounted() {
    this.handleRouteParams(this.$route.query);
  },
  methods: {
    handleRouteParams(queryParameterObject) {
      this.sortColumn = queryParameterObject.o;
      this.ascending = !!(queryParameterObject.a && queryParameterObject.a.toString() === 'true');
      this.currentPage = queryParameterObject.p || 1;
    },
    pageChange(page) {
      this.updateRoute(page, this.sortColumn, this.ascending);
    },
    updateRoute(page, column, ascending) {
      this.$router.push({
        query: { ...this.$route.query, ...paginationHelper(page, column, ascending) },
      });
    },
    sortedData() {
      if (!this.sortColumn) {
        return this.testFixtures;
      }
      return [...this.testFixtures].sort((a, b) => {
        if (a[this.sortColumn] === null) {
          return this.ascending ? 1 : -1;
        } if (b[this.sortColumn] === null) {
          return this.ascending ? -1 : 1;
        } if (a[this.sortColumn] > b[this.sortColumn]) {
          return this.ascending ? 1 : -1;
        } if (a[this.sortColumn] < b[this.sortColumn]) {
          return this.ascending ? -1 : 1;
        }
        return 0;
      });
    },
    sortTable: function sortTable(col) {
      if (this.sortColumn === col) {
        this.ascending = !this.ascending;
      } else {
        this.ascending = true;
        this.sortColumn = col;
      }
      this.updateRoute(this.currentPage, this.sortColumn, this.ascending);
    },
    toggleEnabled(report) {
      this.removeTestFixture(report);
    },
    handleColumnClick(columnDefinition, testFixture) {
      if (columnDefinition.isCheckinLink && testFixture.lastCheckIn) {
        this.showCheckinList(testFixture.id);
      }
    },
    showCheckinList(testFixtureId) {
      this.checkins = [];
      this.testFixtureCheckins(testFixtureId).then((x) => {
        this.checkins = x;
        this.$refs.checkinModal.show();
      });
    },
    verifyUpdateToVersion(testFixture) {
      this.confirmTitle = 'Update Test Fixture Version';
      this.confirmBody = `Are you sure you want to push an update to Test Fixture ${testFixture.serialNumber}?`;
      this.confirmAction = (whenDone) => {
        this.$refs.confirmModal.hide();
        this.updateToLatestVersion(testFixture);
        whenDone();
      };
      this.$refs.confirmModal.show();
    },
  },
};
</script>

<style lang="scss" scoped>

</style>
